import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStoryboard, storyOpened, storyWords, WORKING, type Storyboard } from '../lib/storyboard';
import { PrintTeaser } from './Reveal';
import { track } from '../analytics';
import { castDry, castOff, starring } from '../lib/cast';
import { loadProfile, currentDay } from '../lib/state';

/**
 * The way into a story's storyboard from wherever its post is (Reward, Home).
 * It asks the backend and shows nothing when there is no told story, so it
 * can be dropped anywhere an id is known. Dry and not yet turned over, it is a
 * print face-down like every other (components/Reveal.tsx PrintTeaser); turned
 * over, a small card that opens it again. Failed or too short, a card that
 * says so and opens the retry.
 *
 * Be in your story (well/product/32): a recast drawing says so; a dry one is a
 * gold print, "Starring Sam"; and after one "Not now", a later day's card is
 * "Your part's still open", the teller ringed, which opens the ask again.
 */
export function StoryboardTeaser({ postId }: { postId?: string }) {
  const nav = useNavigate();
  const [sb, setSb] = useState<Storyboard | null>(null);
  useEffect(() => {
    if (!postId) return;
    let stop = false;
    let tries = 0;
    const tick = async () => {
      const s = await getStoryboard(postId).catch(() => null);
      if (stop) return;
      setSb(s);
      if (s && !WORKING.includes(s.status) && s.cast?.status !== 'casting') return;
      // A story kept a moment ago may not have claimed its run yet; after that, none means none.
      if (!s && tries > 5) return;
      if (++tries < 60) setTimeout(tick, 3000);
    };
    tick();
    return () => { stop = true; };
  }, [postId]);

  if (!postId || !sb) return null;
  const open = () => { track('storyboard_opened', { from: 'teaser', status: sb.status }); nav(`/story/${sb.storyId}`, { viewTransition: true }); };
  if (sb.status === 'done' && !storyOpened(sb.storyId)) return <PrintTeaser day={sb.day} words={storyWords(sb)} count={1} onTurn={open} />;
  if (castDry(sb)) return <PrintTeaser day={sb.day} words={starring(sb.cast?.name)} count={1} gold onTurn={() => { track('cast_print_open_from', { from: 'home' }); open(); }} />;
  const profile = loadProfile();
  if (sb.status === 'done' && sb.castable && !castOff() && profile.crew !== 'off' && profile.castDeclined === 1 && currentDay(profile) > sb.day) {
    return (
      <button type="button" className="sb-teaser sb-teaser-part" onClick={() => { track('cast_offered_again', { from: 'home' }); open(); }}>
        <span className="sb-teaser-ring">{sb.pageUrl && <img src={sb.beforePageUrl ?? sb.pageUrl} alt="" />}<i aria-hidden /></span>
        <span className="stack" style={{ gap: 2, textAlign: 'left' }}>
          <span className="label" style={{ color: 'var(--coral-dark)' }}>{sb.title || 'The story you told'}</span>
          <b>Your part’s still open</b>
          <span className="hint">The crew would rather draw you telling it.</span>
        </span>
      </button>
    );
  }
  if (sb.cast?.status === 'casting') {
    return (
      <button type="button" className="sb-teaser" disabled>
        <span className="sb-teaser-blank"><span className="dot pulse" /><span className="dot pulse d2" /><span className="dot pulse d3" /></span>
        <span className="stack" style={{ gap: 2, textAlign: 'left' }}>
          <span className="label" style={{ color: 'var(--green-dark)' }}>{starring(sb.cast.name)}</span>
          <b>Redrawing it with you in it…</b>
        </span>
      </button>
    );
  }
  // It did not come out, or ran short: said so, never left to vanish. /story/:id has the retry.
  if (sb.status === 'failed' || sb.status === 'too-short') {
    const blocked = sb.blocked === 'billing';
    const short = sb.status === 'too-short';
    return (
      <button type="button" className="sb-teaser" onClick={open}>
        <span className="sb-teaser-blank sb-teaser-miss" aria-hidden>{short ? '…' : '?'}</span>
        <span className="stack" style={{ gap: 2, textAlign: 'left' }}>
          <span className="label" style={{ color: 'var(--green-dark)' }}>The story you told</span>
          <b>{blocked ? 'Your crew are out of ink just now' : short ? 'A little short for four panels' : 'The storyboard did not come out'}</b>
          <span className="hint">{short && !sb.error ? 'Tap to see it typed out.' : 'Your story is kept. Tap to have the crew try again.'}</span>
        </span>
      </button>
    );
  }
  const working = WORKING.includes(sb.status);
  return (
    <button type="button" className="sb-teaser" disabled={working} onClick={open}>
      {sb.pageUrl ? <img src={sb.pageUrl} alt="" /> : <span className="sb-teaser-blank"><span className="dot pulse" /><span className="dot pulse d2" /><span className="dot pulse d3" /></span>}
      <span className="stack" style={{ gap: 2, textAlign: 'left' }}>
        <span className="label" style={{ color: 'var(--green-dark)' }}>{working ? 'Your crew are on it' : 'Your story, storyboarded'}</span>
        <b>{working ? 'Developing the story you told…' : sb.title}</b>
        {!working && <span className="hint">Four panels, in your words. Tap to play it.</span>}
      </span>
    </button>
  );
}
